import React from "react";
import { motion } from "framer-motion";
import { fadeUp, softReveal, staggerParent, viewportOnce } from "./motion";

const pillars = [
  {
    title: "Strategy First",
    detail: "We align channels to your business model, growth stage, and sales velocity.",
    tag: "Clarity before execution",
  },
  {
    title: "Execution Discipline",
    detail: "Your campaigns are built, tested, and iterated with clear owners and timelines.",
    tag: "Consistent momentum",
  },
  {
    title: "Transparent Reporting",
    detail: "You see what is working, what is changing, and where revenue impact is coming from.",
    tag: "No black-box marketing",
  },
];

const proofPoints = [
  { value: "4+", label: "Active client sectors" },
  { value: "Full-funnel", label: "Strategy to conversion support" },
  { value: "Weekly", label: "Optimization rhythm" },
];

const processSteps = [
  {
    title: "Diagnose",
    detail: "We identify gaps in traffic quality, conversion flow, and messaging clarity.",
  },
  {
    title: "Build",
    detail: "We shape campaigns, content systems, and web experiences around measurable outcomes.",
  },
  {
    title: "Refine",
    detail: "We optimize continuously so the growth system gets stronger over time.",
  },
];

const currentClients = [
  {
    name: "Sai Hasini Dental Clinic",
    logo: "/client-logos/shdc.png",
    industry: "Healthcare",
  },
  {
    name: "Prime Hospitals",
    logo: "/client-logos/prime-hospitals.png",
    industry: "Healthcare",
  },
  {
    name: "Manasa Veg & Non Veg",
    logo: "/client-logos/manasa.png",
    industry: "Restaurant",
  },
  {
    name: "Vakulaa Tiffins",
    logo: "/client-logos/vakulaa-tiffins.png",
    industry: "Food brand",
  },
];

function AboutUs() {
  return (
    <motion.section
      className="portfolio"
      variants={staggerParent(0.06, 0.12)}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
    >
      <motion.div className="about-hero" variants={softReveal}>
        <div className="about-intro">
          <p className="section-kicker">About dYzital</p>
          <h1>A Practical Digital Marketing Partner for Businesses That Want to Grow</h1>
          <p className="desktop-copy">
            dYzital helps businesses improve their online presence, attract the right audience, and turn more interest into enquiries through clear digital marketing support. We operate from Madhapur, Hyderabad and support businesses across service areas.
          </p>
          <p className="mobile-copy">
            We help businesses improve their online presence and turn more interest into enquiries.
          </p>
        </div>

        <div className="about-story-card">
          <p className="about-story-kicker">What we bring</p>
          <h3>We focus on steady growth, not one-off activity.</h3>
          <p className="desktop-copy">
            Our work connects SEO, paid media, content, website experience, and follow-up support so each part of your digital presence works better together.
          </p>
          <p className="mobile-copy">
            SEO, ads, content, websites, and follow-up systems working together.
          </p>

          <div className="about-proof-strip">
            {proofPoints.map((point) => (
              <div key={point.label} className="about-proof-card">
                <strong>{point.value}</strong>
                <span>{point.label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div className="pillar-grid" variants={staggerParent(0.12, 0.1)}>
        {pillars.map((pillar) => (
          <motion.article
            key={pillar.title}
            className="pillar-card"
            variants={softReveal}
            whileHover={{ y: -8, scale: 1.01 }}
            transition={{ duration: 0.22 }}
          >
            <p className="pillar-tag">{pillar.tag}</p>
            <h3>{pillar.title}</h3>
            <p>{pillar.detail}</p>
          </motion.article>
        ))}
      </motion.div>

      <motion.div className="about-process" variants={fadeUp}>
        <div className="portfolio-note">
          <h2>How We Work</h2>
          <p className="desktop-copy">
            We keep the process simple, collaborative, and focused on business results instead of confusing marketing jargon.
          </p>
          <p className="mobile-copy">
            A simple process focused on business results.
          </p>
        </div>

        <div className="process-grid">
          {processSteps.map((step, index) => (
            <motion.article key={step.title} className="process-card" variants={softReveal}>
              <span className="process-index">0{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.detail}</p>
            </motion.article>
          ))}
        </div>
      </motion.div>

      <motion.div className="portfolio-note" variants={fadeUp}>
        <h2>Clients We Support</h2>
        <p className="desktop-copy">
          We work with businesses that want dependable digital marketing support, clear communication, and consistent execution.
        </p>
        <p className="mobile-copy">
          Dependable support, clear communication, and consistent execution.
        </p>
      </motion.div>

      <motion.div className="client-logos-section" variants={softReveal}>
        <h3>Current Client Snapshot</h3>
        <div className="client-logos-marquee" aria-label="Current client logos">
          <div className="client-logos-track">
            {[...currentClients, ...currentClients].map((client, index) => (
              <article key={`${client.name}-${index}`} className="client-logo-card">
                <img src={client.logo} alt={`${client.name} logo`} loading="lazy" />
                <p>{client.name}</p>
                <span>{client.industry}</span>
              </article>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
}

export default AboutUs;
